import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useWaterTestData } from '@/hooks/useWaterTestData';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { measurePerformance } from '@/utils/performance';
import { calculateTargetRangeAxis } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useEffect, useMemo, useRef } from 'react';
import { WaterTestPoint } from '@/types/api';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { DEVICE_COLORS, CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import type { ChartAnnotation } from '@/types/chartAnnotation';

interface AlkalinityChartProps {
  startDate: Date;
  endDate: Date;
  /** Pre-fetched water test data for alkalinity (dKH + KH combined) */
  waterTestData?: Array<{ timestamp: string; param_name: string; value: number; device: string; note?: string }>;
  /** Pre-fetched target ranges for axis scaling */
  targetRangesData?: Array<{ parameterId: string; minValue: number | null; maxValue: number | null; unit: string }>;
  isLoading?: boolean;
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
  /** Annotations to display on the chart */
  annotations?: ChartAnnotation[];
  /** Callback when the "Add note" button is clicked */
  onAddNote?: () => void;
  /** Callback when an annotation is clicked for editing */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

const ALK_DEVICES = ['aquawiz', 'alkatronic', 'aquarimate', 'mastertronic', 'manual', 'kamoer_kh'] as const;
type AlkDevice = typeof ALK_DEVICES[number];

interface ChartDataPoint {
  timestamp: number;
  aquawiz?: number;
  alkatronic?: number;
  aquarimate?: number;
  mastertronic?: number;
  manual?: number;
  kamoer_kh?: number;
  [key: `${string}_note`]: string | undefined;
}

export function AlkalinityChart({
  startDate,
  endDate,
  waterTestData,
  targetRangesData,
  isLoading: parentLoading,
  error: parentError,
  expanded,
  onToggle,
  annotations,
  onAddNote,
  onAnnotationClick,
}: AlkalinityChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Responsive hooks
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Fetch dKH data (alkatronic, mastertronic, aquarimate) - used as fallback
  // Disable when parent provides data OR parent is loading
  const { data: dkhData, isLoading: dkhLoading, error: dkhError } = useWaterTestData({
    startDate,
    endDate,
    paramName: 'dKH',
    enabled: !waterTestData && parentLoading !== true,
  });

  // Fetch KH data (aquawiz) - used as fallback
  // Disable when parent provides data OR parent is loading
  const { data: khData, isLoading: khLoading, error: khError } = useWaterTestData({
    startDate,
    endDate,
    paramName: 'KH',
    enabled: !waterTestData && parentLoading !== true,
  });

  // Use parent state if provided, otherwise combine from hooks
  const isLoading = waterTestData !== undefined ? (parentLoading ?? false) : (dkhLoading || khLoading);
  const error = waterTestData !== undefined ? parentError : (dkhError || khError);

  // Combine data from parent prop or both queries
  const data = useMemo(() => {
    // If parent provides data, filter for alkalinity parameters
    // Supports both legacy names (dKH, KH) and unified name (alkalinity)
    if (waterTestData) {
      const alkalinityData = waterTestData.filter(
        p => p.param_name === 'dKH' || p.param_name === 'KH' || p.param_name === 'alkalinity'
      );
      return {
        data: alkalinityData,
        count: alkalinityData.length,
        performance: { query_ms: 0 },
      };
    }

    // Fallback: combine data from hooks
    if (!dkhData && !khData) return null;

    const combinedData = [
      ...(dkhData?.data || []),
      ...(khData?.data || []),
    ];

    return {
      data: combinedData,
      count: combinedData.length,
      performance: {
        query_ms: (dkhData?.performance?.query_ms || 0) + (khData?.performance?.query_ms || 0),
      },
    };
  }, [waterTestData, dkhData, khData]);

  // Measure chart render performance
  useEffect(() => {
    if (data && !isLoading) {
      const measure = measurePerformance('AlkalinityChart');
      const timerId = setTimeout(() => measure.end(), 0);
      return () => clearTimeout(timerId);
    }
  }, [data, isLoading]);

  // Transform data for Recharts - combine all devices by timestamp
  const chartData = useMemo(() => {
    if (!data?.data) return [];

    // Group by timestamp and device - round to nearest hour for alignment
    const grouped = new Map<number, ChartDataPoint>();

    data.data.forEach((point: WaterTestPoint) => {
      const date = new Date(point.timestamp);
      // Round to nearest hour for alignment across all devices
      const roundedTimestamp = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        0, 0, 0
      ).getTime();

      if (!grouped.has(roundedTimestamp)) {
        grouped.set(roundedTimestamp, { timestamp: roundedTimestamp });
      }

      const entry = grouped.get(roundedTimestamp)!;

      // Map device to property name and carry note if present
      if ((ALK_DEVICES as readonly string[]).includes(point.device)) {
        const deviceKey = point.device as AlkDevice;
        entry[deviceKey] = point.value;
        if (point.note) entry[`${deviceKey}_note`] = point.note;
      }
    });

    // Convert to array and sort by timestamp
    return Array.from(grouped.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [data]);

  // Range-aware X-axis: dates for multi-day, times for single-day
  const timeAxis = useTimeAxis(startDate, endDate);

  // Find alkalinity target range (check 'alkalinity' and 'dkh' keys)
  // This drives the Y-axis: no hardcoded defaults needed since target ranges
  // come from user config or global defaults in the database
  const alkRange = useMemo(() => {
    return targetRangesData?.find(
      r => r.parameterId === 'alkalinity' || r.parameterId === 'dkh'
    );
  }, [targetRangesData]);

  // Calculate Y-axis from target ranges + data (no hardcoded axis bounds)
  const yAxisConfig = useMemo(() => {
    const values: number[] = [];
    chartData.forEach((d) => {
      if (d.aquawiz != null) values.push(d.aquawiz);
      if (d.alkatronic != null) values.push(d.alkatronic);
      if (d.aquarimate != null) values.push(d.aquarimate);
      if (d.mastertronic != null) values.push(d.mastertronic);
      if (d.manual != null) values.push(d.manual);
      if (d.kamoer_kh != null) values.push(d.kamoer_kh);
    });

    const hasValues = values.length > 0;

    return calculateTargetRangeAxis({
      dataMin: hasValues ? Math.min(...values) : undefined,
      dataMax: hasValues ? Math.max(...values) : undefined,
      targetMin: alkRange?.minValue ?? undefined,
      targetMax: alkRange?.maxValue ?? undefined,
    });
  }, [chartData, alkRange]);

  // Detect which data sources have data for conditional legend rendering
  const hasAquawiz = chartData.some((d) => d.aquawiz != null);
  const hasAlkatronic = chartData.some((d) => d.alkatronic != null);
  const hasMastertronic = chartData.some((d) => d.mastertronic != null);
  const hasAquarimate = chartData.some((d) => d.aquarimate != null);
  const hasManual = chartData.some((d) => d.manual != null);
  const hasKamoer = chartData.some((d) => d.kamoer_kh != null);

  if (isLoading) {
    return <ChartSkeleton title="Alkalinity (dKH)" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading alkalinity data">
        {error instanceof Error
          ? error.message
          : JSON.stringify(error, null, 2)}
      </Alert>
    );
  }

  const hasData = chartData.length > 0 && yAxisConfig !== null;

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">

      <ChartHeader
        title="Alkalinity"
        subtitle={isMobile ? '(dKH)' : (data?.performance ? `(${data.count} tests, ${data.performance.query_ms}ms)` : undefined)}
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
        onAddNote={onAddNote}
      />

      {!hasData && !isLoading && (
        <Alert color="blue" title="No data available">
          No alkalinity test data found for the selected date range.
        </Alert>
      )}

      {hasData && (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

            {/* Annotation regions - rendered behind data lines */}
            {annotations && (
              <ChartAnnotationLayer
                annotations={filterAnnotationsForChart(
                  annotations,
                  'alkalinity',
                  ['alkatronic', 'mastertronic', 'aquawiz', 'aquarimate', 'manual', 'kamoer_kh']
                )}
                onAnnotationClick={onAnnotationClick}
              />
            )}

            <XAxis
              dataKey="timestamp"
              type="number"
              domain={[startDate.getTime(), new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).getTime()]}
              ticks={timeAxis.ticks}
              tickFormatter={timeAxis.formatTick}
              tick={CHART_AXIS_TICK}
              axisLine={false}
              tickLine={false}
              height={isMobile ? 35 : 50}
            />

            <YAxis
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              tickFormatter={yAxisConfig.tickFormatter}
              width={isMobile ? 35 : 60}
              tick={CHART_AXIS_TICK}
              axisLine={false}
              tickLine={false}
            />

            {/* Crosshair tooltip with custom content */}
            <Tooltip
              cursor={{
                stroke: CHART_CROSSHAIR_COLOR,
                strokeWidth: 1,
                strokeDasharray: '4 4'
              }}
              content={
                <ChartTooltipContent
                  valueFormatter={(v) => `${v.toFixed(2)} dKH`}
                />
              }
            />

            <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

            {/* AquaWiz line (purple) - continuous monitoring - only show if has data */}
            {hasAquawiz && (
              <Line
                type="monotone"
                dataKey="aquawiz"
                stroke={DEVICE_COLORS.aquawiz}
                strokeWidth={2}
                dot={{ r: 3, fill: DEVICE_COLORS.aquawiz }}
                name="AquaWiz"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}

            {/* Alkatronic line (orange) - continuous monitoring - only show if has data */}
            {hasAlkatronic && (
              <Line
                type="monotone"
                dataKey="alkatronic"
                stroke={DEVICE_COLORS.alkatronic}
                strokeWidth={2}
                dot={{ r: 3, fill: DEVICE_COLORS.alkatronic }}
                name="Alkatronic"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}

            {/* Mastertronic line (red) - discrete tests - only show if has data */}
            {hasMastertronic && (
              <Line
                type="monotone"
                dataKey="mastertronic"
                stroke={DEVICE_COLORS.mastertronic}
                strokeWidth={1.5}
                dot={{ r: 4, fill: DEVICE_COLORS.mastertronic }}
                name="Mastertronic"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}

            {/* Aquarimate line (blue) - tests - only show if has data */}
            {hasAquarimate && (
              <Line
                type="monotone"
                dataKey="aquarimate"
                stroke={DEVICE_COLORS.aquarimate}
                strokeWidth={1.5}
                dot={{ r: 4, fill: DEVICE_COLORS.aquarimate, strokeWidth: 0 }}
                name="Aquarimate"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}

            {/* Kamoer KH Carer line - only show if has data */}
            {hasKamoer && (
              <Line
                type="monotone"
                dataKey="kamoer_kh"
                stroke={DEVICE_COLORS.kamoer_kh}
                strokeWidth={2}
                dot={{ r: 3, fill: DEVICE_COLORS.kamoer_kh }}
                name="Kamoer KH"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}

            {/* Manual entry line (purple) - user-entered data - only show if has data */}
            {hasManual && (
              <Line
                type="monotone"
                dataKey="manual"
                stroke={DEVICE_COLORS.manual}
                strokeWidth={1.5}
                dot={{ r: 4, fill: DEVICE_COLORS.manual, strokeWidth: 0 }}
                name="Manual"
                activeDot={{ r: 6 }}
                connectNulls={true}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}
